import React from 'react';
import Header from './shared/Header';
import Footer from './Footer';
import SEO from './SEO';
import {
  BuildingOfficeIcon,
  ChartBarIcon,
  ArrowPathIcon,
  CubeIcon,
  ClockIcon,
  BanknotesIcon
} from '@heroicons/react/20/solid';

const blackRockFeatures = [
  {
    name: 'ETF-Based Approach',
    icon: CubeIcon,
    description: 'BlackRock model portfolios primarily use ETFs and mutual funds, focusing on broad market exposure and traditional asset allocation strategies.',
    details: [
      "Diversified ETF holdings",
      "Broad market exposure",
      "Traditional asset allocation",
      "Passive investment approach"
    ]
  },
  {
    name: 'Periodic Rebalancing',
    icon: ArrowPathIcon,
    description: 'BlackRock portfolio value is maintained through calendar-based rebalancing, typically quarterly or annually, depending on the strategy.',
    details: [
      "Calendar-based rebalancing",
      "Quarterly or annual frequency",
      "Systematic approach",
      "Risk management focus"
    ]
  },
  {
    name: 'Traditional Data Sources',
    icon: ChartBarIcon,
    description: 'BlackRock average returns are based on traditional market data, fundamental analysis, and macroeconomic factors.',
    details: [
      "Market data analysis",
      "Fundamental research",
      "Macroeconomic factors",
      "Traditional metrics"
    ]
  }
];

const comparisonData = [
  {
    feature: 'Investment Philosophy',
    blackrock: 'Passive, market-tracking',
    sharpemetrix: 'Active, alpha-seeking'
  },
  {
    feature: 'Primary Instruments',
    blackrock: 'ETFs, mutual funds',
    sharpemetrix: 'Individual stocks'
  },
  {
    feature: 'Rebalancing Strategy',
    blackrock: 'Calendar-based',
    sharpemetrix: 'Signal-driven'
  },
  {
    feature: 'Data Sources',
    blackrock: 'Traditional market data',
    sharpemetrix: 'Alternative data signals'
  },
  {
    feature: 'Risk Management',
    blackrock: 'Diversification-based',
    sharpemetrix: 'Volatility-adjusted'
  },
  {
    feature: 'Target Return',
    blackrock: 'Market performance',
    sharpemetrix: 'Excess return (alpha)'
  },
  {
    feature: 'Customization',
    blackrock: 'Limited',
    sharpemetrix: 'High flexibility'
  }
];

export default function BlackRockComparisonPage() {
  return (
    <>
      <SEO 
        title="BlackRock Model Portfolios vs SharpeMetrix: TAMP Comparison"
        description="Compare BlackRock model portfolios with SharpeMetrix TAMP solutions. See how our alternative data approach differs from BlackRock's ETF-based strategies."
        keywords="BlackRock model portfolios, BlackRock portfolio value, BlackRock average returns, BlackRock vs SharpeMetrix, TAMP comparison"
        url="/blackrock-model-portfolios"
      />
      <Header />
      <div className="min-h-screen bg-bg-100">
        <main className="relative isolate">
          {/* Background Gradient */}
          <div
            className="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-[#305CFE] to-[#02D3FF] opacity-20"
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
              }}
            />
          </div>

          {/* Header Section */}
          <div className="px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-3xl pt-24 text-center sm:pt-40 pb-16 sm:pb-24">
              <h1 className="text-4xl font-bold tracking-tight text-text-100 sm:text-6xl">
                BlackRock Model Portfolios vs SharpeMetrix
              </h1>
              <p className="mt-6 text-xl leading-8 text-text-200">
                Understand the key differences between BlackRock model portfolios and SharpeMetrix TAMP solutions. See how our alternative data approach creates unique advantages for financial advisors.
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-24 sm:pb-32">

            {/* BlackRock Overview Section */}
            <section className="mb-16 lg:mb-24">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                  <BuildingOfficeIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                  Understanding BlackRock Model Portfolios
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  BlackRock model portfolios are created with ETFs, mutual funds, SMAs and alternatives to help clients meet investment goals from capital preservation to growth and income.
                </p>
              </div>
            </section>

            {/* BlackRock Features Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 mb-16 lg:mb-24 ring-1 ring-white/10 shadow-lg">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                BlackRock's Approach
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 lg:grid-cols-3 lg:gap-y-16">
                {blackRockFeatures.map((feature) => (
                  <div key={feature.name} className="flex flex-col items-start">
                    <div className="flex items-center gap-x-3 mb-4">
                      <feature.icon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                      <h3 className="text-xl font-semibold leading-7 text-text-100">{feature.name}</h3>
                    </div>
                    <p className="text-base leading-7 text-text-200 mb-3">{feature.description}</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text-300 pl-2">
                      {feature.details.map((detail, index) => <li key={index}>{detail}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Detailed Comparison Section */}
            <section className="mb-16 lg:mb-24">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                BlackRock vs SharpeMetrix: Detailed Comparison
              </h2>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg bg-bg-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-bg-300">
                    <thead className="bg-bg-100">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-text-100">Feature</th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">BlackRock Model Portfolios</th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">SharpeMetrix</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bg-300 bg-bg-200">
                      {comparisonData.map((item, index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-text-100">{item.feature}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-center">{item.blackrock}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-center">{item.sharpemetrix}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* SharpeMetrix Advantages Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 mb-16 lg:mb-24 ring-1 ring-white/10 shadow-lg">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                SharpeMetrix Advantages
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 lg:grid-cols-3 lg:gap-y-16">
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-x-3 mb-4">
                    <ChartBarIcon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                    <h3 className="text-xl font-semibold leading-7 text-text-100">Alternative Data Edge</h3>
                  </div>
                  <p className="text-base leading-7 text-text-200">
                    While BlackRock relies on traditional market data, SharpeMetrix leverages proprietary alternative data signals to identify unique investment opportunities before they're reflected in conventional metrics.
                  </p>
                </div>
                
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-x-3 mb-4">
                    <ClockIcon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                    <h3 className="text-xl font-semibold leading-7 text-text-100">Dynamic Rebalancing</h3>
                  </div>
                  <p className="text-base leading-7 text-text-200">
                    Unlike BlackRock's calendar-based approach, our signal-driven rebalancing responds to market conditions in real-time, potentially capturing opportunities as they emerge.
                  </p>
                </div>
                
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-x-3 mb-4">
                    <BanknotesIcon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                    <h3 className="text-xl font-semibold leading-7 text-text-100">Alpha Generation Focus</h3>
                  </div>
                  <p className="text-base leading-7 text-text-200">
                    While BlackRock focuses on market-tracking performance, SharpeMetrix is designed to generate alpha through systematic identification of mispriced securities using alternative data.
                  </p>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 ring-1 ring-white/10 shadow-lg">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl">
                  Ready to Move Beyond Traditional ETF Strategies?
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  Discover how SharpeMetrix's alternative data approach can complement or enhance your existing BlackRock allocations.
                </p>
                <div className="mt-10 flex items-center justify-center gap-x-6">
                  <a
                    href="/register"
                    className="inline-flex items-center gap-x-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                  >
                    Start Free 30-Day Trial
                  </a>
                  <a href="/tamp-comparison" className="text-sm font-semibold leading-6 text-text-200">
                    View All Comparisons <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
} 